import React, {FC, useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {useNavigate} from 'react-router-dom'
import {FavoriteButton, LikeButton} from 'entities/activity'
import {setProfileChatOpen, useProfileChat} from 'entities/app'
import {PhotoTypes, useChatCanViewPrivatePhotos, useChatPairConnection} from 'entities/messages'
import {
  clearStateGallery,
  clearStateProfileMedia,
  GetProfileMedia,
  openGallery,
  setCurrentImage,
  setGalleryPhotos,
  useGallery,
  useProfile,
  useProfileMedia,
  useProfileMediaCursor
} from 'entities/profile'
import {TextSpoiler} from 'features/textSpoiler'
import {useVirtualScroll} from 'shared/hooks'
import {countriesMap, mediaVariants} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {Cross, Lock} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

export const Profile = () => {
  const dispatch = useDispatch()
  const {id} = useParams()
  const isOpen = useProfileChat()
  const ref = useRef<HTMLDivElement | null>(null)
  const profile = useProfile()

  useEffect(() => {
    dispatch(clearStateProfileMedia())
    dispatch(clearStateGallery())
  }, [id])

  if (!id || !isOpen || profile.isBlocked) return <></>

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.close} onClick={() => dispatch(setProfileChatOpen(false))}>
          <Cross />
        </div>
        Profile
      </div>
      {profile.id
        ? <div className={`${styles.wrapper} ${styles.customScroll}`} ref={ref}>
          <ProfileSummary />
          {!profile.isBlocked ? <Media wrapper={ref} /> : null}
        </div>
        : <div className={styles.loader}><Loader /></div>}
    </div>
  )
}

const ProfileSummary = () => {
  const navigate = useNavigate()
  const profile = useProfile()
  const activity = useChatPairConnection()

  const openProfileHandler = () => navigate(`/profile/${profile.id}`)

  return (
    <>
      <div className={styles.avatarWrapper}>
        <img
          className={styles.avatar}
          src={profile.videoAvatarUrl || profile.avatarUrl + mediaVariants.rectangle512}
          alt='avatar'
        />
        <div className={styles.background}></div>
        <div className={styles.info}>
          <div className={styles.infoHeader}>
            <div className={styles.name}>{profile.name},</div>
            <div>{profile.age}</div>
          </div>
          <div className={styles.country}>
            {profile.country ? countriesMap[profile.country] : ''}
          </div>
        </div>
        <FavoriteButton className={styles.favorite} value={activity.isFavorite} idUser={profile.id} />
        <LikeButton className={styles.like} value={activity.isLike} idUser={profile.id} />
      </div>
      {!profile.isBlocked ? <>
        <Button styleType='primary' onClick={openProfileHandler}>Open profile</Button>
        <TextSpoiler text={profile.about || ''} />
      </> : null}
    </>
  )
}

const Media: FC<{wrapper: React.MutableRefObject<HTMLDivElement | null>, onClick?: VoidFunction}> = ({wrapper, onClick}) => {
  const {id} = useParams()
  const dispatch = useDispatch()
  const cursor = useProfileMediaCursor()
  const media = useProfileMedia()
  const {refetch: load, isFetching, isError} = GetProfileMedia(id || '', dispatch)
  const {galleryPhotos: photos} = useGallery()
  const isCanViewPrivatePhotos = useChatCanViewPrivatePhotos()

  useEffect(() => {
    dispatch(setGalleryPhotos({photos: media.all, cursor}))
  }, [media])

  useVirtualScroll({ref: wrapper, isError, isFetching, load, cursor})

  const openGalleryHandler = (type: PhotoTypes, index: number) => {
    onClick && onClick()
    dispatch(openGallery())
    dispatch(setCurrentImage({
      index,
      type,
      src: photos[index].image.url,
      id: photos[index].id
    }))
  }

  return (
    <div className={styles.mediaWrapper}>
      <div className={styles.mediaHeader}>
        Media
      </div>
      <div className={styles.mediaList}>
        {media.all.map((item, index) =>
          <picture className={`${styles.media} ${item.type === PhotoTypes.private && !isCanViewPrivatePhotos ? styles.private : ''}`} key={index} onClick={() => openGalleryHandler(item.type, index)}>
            <img src={item.image.url + mediaVariants.square128} alt='photo' loading='lazy' />
            {item.type === PhotoTypes.private && !isCanViewPrivatePhotos ? <div className={styles.lock}><Lock /></div> : null}
          </picture>
        )}
      </div>
      {isFetching ? <>Loading...</> : null}
    </div>
  )
}
